"use client";

import type {
  ISchedule,
  WorkplaceRequestElement,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { getScheduleRooms } from "@gettactic/helpers/src/schedules/getScheduleRooms";
import React, { useState } from "react";
import { useDeskMove } from "@/features/workplace-requests/hooks/useDeskAutomation";
import { useScheduleById } from "../RequestDetailsModal/ScheduleInfoCard";
import { DeskMoveSection } from "./DeskMoveSection";
import {
  getRequestMapLocation,
  LocationOnMapPanel
} from "./LocationOnMapPanel";
import { RequestRoomsPanel } from "./RequestRoomsPanel";

interface RequestLocationSectionProps {
  request: WorkplaceRequestElement;
  /** The request's type, whose desk rules say whether this is a relocation. */
  type?: WorkplaceRequestTypeElement;
  officeName: string;
  /** Reservation behind the request, when the caller already has it. */
  schedule?: ISchedule | null;
}

/**
 * "Location on map" plus, when the reservation behind the request holds several meeting
 * rooms, the list of those rooms. The map shows one room at a time and the list picks
 * which one.
 *
 * A request that relocates someone shows both ends of the move instead: the desk they
 * give up next to the one they take.
 */
export const RequestLocationSection: React.FC<RequestLocationSectionProps> = ({
  request,
  type,
  officeName,
  schedule: passedSchedule
}) => {
  // Shares the ["schedule", id] cache with the sidebar's card, so this costs no request.
  const { data: fetchedSchedule } = useScheduleById(
    passedSchedule ? null : (request.schedule_id ?? null)
  );
  const schedule = passedSchedule ?? fetchedSchedule ?? null;

  const deskMove = useDeskMove(request, type);

  const rooms = getScheduleRooms(schedule);
  const isMultiRoom = rooms.length > 1;

  // The section is reused as the queue navigates between requests, so the pick carries
  // the request it was made on. Matching on the room id alone let a pick from a
  // sibling request of the same reservation override the room this ticket points at.
  const [pickedRoom, setPickedRoom] = useState<{
    requestId: string;
    roomId: string;
  } | null>(null);
  const selectRoom = React.useCallback(
    (roomId: string) => setPickedRoom({ requestId: request.id, roomId }),
    [request.id]
  );
  // The room the ticket itself points at leads.
  const requestRoomId = getRequestMapLocation(request).resourceId;
  const defaultRoomId =
    rooms.find((room) => room.id === requestRoomId)?.id ?? rooms[0]?.id ?? null;
  const selectedRoomId =
    (pickedRoom?.requestId === request.id
      ? rooms.find((room) => room.id === pickedRoom.roomId)?.id
      : undefined) ?? defaultRoomId;

  if (deskMove) {
    return <DeskMoveSection move={deskMove} />;
  }

  return (
    <>
      <LocationOnMapPanel
        request={request}
        officeName={officeName}
        focusResourceId={isMultiRoom ? selectedRoomId : null}
      />
      {isMultiRoom && (
        <RequestRoomsPanel
          rooms={rooms}
          selectedRoomId={selectedRoomId}
          onSelectRoom={selectRoom}
          officeName={officeName}
        />
      )}
    </>
  );
};
